/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback, useState } from "react";
import { useAuth } from "cms/auth/hooks/use-auth";
// import {Logger} from 'cms/utils/log/logger'

const EMPTY_COUNT = 1;
const FIRST_PAGE = 1;
const DEFAULT_PAGES = 1;
const DEFAULT_PAGE_SIZE = 20;

const useAsyncSearch = (
    mounted,
    filterValue,
    searchFunction,
    setItems,
    setNeedsRefresh,
) => {
    
    const { token } = useAuth();
    const [term, setTerm] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [count, setCount] = useState(EMPTY_COUNT);
    const [page, setPage] = useState(FIRST_PAGE);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pages, setPages] = useState(DEFAULT_PAGES);

    const handleSearch = async (event) => {
        if (mounted) {
            const term = event?.target?.value;
            if (term) {
                setTerm(term);
                // const {results, pagination} = searchResults
                // setItems(results)
                // if (pagination) {
                //     setCount(pagination?.total);
                //     setPage(pagination?.page);
                //     setPages(pagination?.pages);
                //     setPageSize(pagination?.pageSize);
                // }
            } else {
                setTerm("");
            }
            await fetchContent(token, term, filterValue, page, pageSize);
        } else {
            console.log("not mounted");
        }
    };
    
    const fetchContent = useCallback(
        async (token, term, filterValue, page, pageSize) => {
            
            let searchResults;
            if (mounted) {
                const options = { page, pageSize };
                if (!token) {
                    throw Error("no auth token during search");
                }
                if (typeof searchFunction !== "function") {
                    throw Error("searchFunction is not a Function.");
                }
                searchResults = await searchFunction(token, term, options, filterValue);
                let results = searchResults?.results;
                const pagination = searchResults?.pagination;
                if (pagination) {
                    setCount(pagination?.total);
                    setPage(pagination?.page);
                    setPages(pagination?.pages);
                    setPageSize(pagination?.pageSize);
                } else {
                    results = searchResults;
                }
                setItems(results); // will fail if not mounted
            } else {
                console.log("not mounted");
            }
            return searchResults;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchFunction, setItems],
    );
    const runFetchContent = async function (mounted) {
        if (mounted) {
            const term = "ALL_RESULTS";
            const searchResults = await fetchContent(token, term, filterValue, page, pageSize);
            const { pagination, results } = searchResults;
            if (pagination) {
                setItems(results); // will fail if not mounted
                setCount(pagination.total);
                setPage(pagination.page);
                setPages(pagination.pages);
                setPageSize(pagination.pageSize);
            }
            console.info("search-filter-list loaded");
            setLoaded(true);
        } else {
            console.log("not mounted");
        }
    };
    
    const getContent = async (term, mounted) => {
        if (mounted) {
            setTerm(term);
            const searchResults = await fetchContent(token, term, filterValue, page, pageSize);
            const { results, pagination } = searchResults;
            setItems(results); // will fail if not mounted
            setCount(pagination.total);
            setPage(pagination.page);
            setPages(pagination.pages);
            setPageSize(pagination.pageSize);
            setNeedsRefresh(false);
            setLoaded(true);
        } else {
            console.log("not mounted");
        }
    };
    
    const handlePageSizeChange = async (event, element) => {
        const size = element.props.value;
        let request_page = page;
        setPageSize(size);
        const expected_pages = count / size + (count % size > 0 ? 1 : 0);
        if (page > expected_pages) {
            setPage(FIRST_PAGE);
            request_page = FIRST_PAGE;
        }
        const searchTerm = term ? term : "ALL_RESULTS";
        const searchResults = await fetchContent(token, searchTerm, filterValue, request_page, size);
        const { results, pagination } = searchResults;
        setItems(results); // will fail if not mounted
        setCount(pagination.total);
        setPage(pagination.page);
        setPages(pagination.pages);
        setPageSize(pagination.pageSize);
    };
    
    const selectPage = async (event, page) => {
        setPage(page);
        const searchTerm = term ? term : "ALL_RESULTS";
        const searchResults = await fetchContent(token, searchTerm, filterValue, page, pageSize);
        const { results, pagination } = searchResults;
        setItems(results);
        setCount(pagination.total);
        setPage(pagination.page);
        setPages(pagination.pages);
        setPageSize(pagination.pageSize);
    };
    
    return {
        term,
        setTerm,
        page,
        pageSize,
        pages,
        loaded,
        // setLoaded,
        handlePageSizeChange,
        selectPage,
        fetchContent,
        runFetchContent,
        handleSearch,
        getContent,
    };
};

export { useAsyncSearch };
